import { FC, useEffect, useState, lazy } from 'react'

import * as Sentry from '@sentry/react'
import { useSearchParams } from 'react-router-dom'

import { CredentialsForm, CredentialsFormValues } from './CredentialsForm'
import { MfaForm, MfaFormValues } from './MfaForm'
import { AlertError, ErrorProps } from '../../components/AlertError'
import { Loader } from '../../components/Loader'
import { isAffiliate } from '../../services/api/affiliates'
import { exchangeToken, signInPassword, signInAuthorizationCode, ssoInit } from '../../services/api/auth'
import { clearSavedTokens, getRefreshToken, saveTokens } from '../../services/authentication'
import { getRecaptchaToken } from '../../services/recaptcha'
import { environmentVariables } from '../../utils/environmentVariables'
import { FailedAuthenticationError, MfaRequiredError, TooManyAttemptsError, UserFacingError } from '../../utils/errors'
import { redirectToUrl } from '../../utils/location'

const Layout = lazy(() =>
  import('../../components/Shared/Layout').then((module) => ({
    default: module.Layout,
  })),
)

type SignInOptions = {
  isSso?: boolean
  username: string
  password: string
  authenticationCode?: string
  secondaryFactor?: string
  redirectTo?: string
}

type SignInPageProps = {
  initialShowAuthenticationCodeForm?: boolean
  initialSignInOptions?: SignInOptions
  initialGlobalErrorMessage?: ErrorProps | null
}

export const SignInPage: FC<SignInPageProps> = ({
  initialShowAuthenticationCodeForm = false,
  initialSignInOptions = { username: '', password: '' },
  initialGlobalErrorMessage = null,
}: SignInPageProps): JSX.Element | null => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showAuthenticationCodeForm, setShowAuthenticationCodeForm] = useState(initialShowAuthenticationCodeForm)
  const [signInOptions, setSignInOptions] = useState(initialSignInOptions)
  const [globalErrorMessage, setGlobalErrorMessage] = useState(initialGlobalErrorMessage)

  const handleError = (error: Error | null): void => {
    if (error === null) {
      setGlobalErrorMessage(null)
      return
    }
    if (error instanceof MfaRequiredError) {
      setShowAuthenticationCodeForm(true)
      return
    }

    if (error instanceof TooManyAttemptsError) {
      setGlobalErrorMessage({ title: '', message: 'sign_in_page.form.error.too_many_attempts' })
      return
    }

    if (error instanceof UserFacingError) {
      setGlobalErrorMessage({ title: error.titleKey, message: error.messageKey })
      return
    }

    setGlobalErrorMessage({ title: 'global.error_title', message: 'global.error_message' })
    throw error
  }

  const handleSubmitError = (error: Error, messageKey: string): void => {
    const submitError = error instanceof FailedAuthenticationError ? new UserFacingError('', messageKey) : error
    handleError(submitError)
  }

  const saveTokensAndRedirect = async (accessToken: string, refreshToken: string | null): Promise<void> => {
    saveTokens(accessToken, refreshToken)
    redirectToUrl(await redirectionAfterSignInUrl(accessToken))
  }

  const redirectionAfterSignInUrl = async (accessToken: string): Promise<string> => {
    const originParamUrlValue = originParamUrl()
    if (originParamUrlValue) {
      return originParamUrlValue
    } else if (await isAffiliate(accessToken)) {
      return environmentVariables('AFFILIATES_APP_URL')
    } else {
      return environmentVariables('EMPLOYEE_APP_URL')
    }
  }

  const originParamUrl = (): string | null => {
    const originParam = searchParams.get('origin')
    try {
      if (originParam) {
        if (new URL(originParam).hostname.endsWith('swile.co')) {
          return originParam
        } else {
          console.error(`Trying to redirect to unauthorized url: '${originParam}'`)
        }
      }
    } catch (TypeError) {
      console.error(`Trying to redirect to invalid url: '${originParam}'`)
    }

    return null
  }

  const isPartnerSignIn = (): boolean => searchParams.get('partner') === 'true'

  // We pass credentials in params for Formik to avoid manually managing the isSubmitting.
  const submitSignIn = async (credentials: SignInOptions): Promise<void> => {
    setGlobalErrorMessage(null)
    const { isSso, authenticationCode, username, password, secondaryFactor } = credentials
    searchParams.delete('error_code')
    setSearchParams(searchParams)

    const recaptchaToken = await getRecaptchaToken('login')
    if (isSso) {
      ssoInit(username, recaptchaToken)
    } else if (isPartnerSignIn()) {
      const redirectTo = searchParams.get('redirect_to') || ''
      const url = await signInAuthorizationCode(
        username,
        password,
        recaptchaToken,
        redirectTo,
        authenticationCode,
        secondaryFactor,
      )
      redirectToUrl(url.redirect_to)
    } else {
      const tokens = await signInPassword(username, password, recaptchaToken, authenticationCode, secondaryFactor)
      await saveTokensAndRedirect(tokens.access_token, tokens.refresh_token)
    }
  }

  const onCredentialsFormSubmit = async (values: CredentialsFormValues): Promise<void> => {
    const newSignInOptions = { username: values.username, password: values.password, isSso: values.isSso }
    setSignInOptions(newSignInOptions)
    try {
      await submitSignIn(newSignInOptions)
    } catch (error) {
      handleSubmitError(error as Error, 'sign_in_page.form.error.wrong_authentication')
    }
  }

  const onMfaFormSubmit = async (values: MfaFormValues): Promise<void> => {
    const newSignInOptions = {
      ...signInOptions,
      authenticationCode: values.authenticationCode,
      secondaryFactor: values.secondaryFactor,
    }
    setSignInOptions(newSignInOptions)
    try {
      await submitSignIn(newSignInOptions)
    } catch (error) {
      handleSubmitError(error as Error, 'sign_in.form.mfa.error.wrong_authentication_code')
    }
  }

  useEffect(() => {
    // Get server errors from URL
    const errorCode = searchParams.get('error_code')
    if (errorCode?.length) {
      if (errorCode === 'RECAPTCHA_INVALID_TOKEN') {
        setGlobalErrorMessage({ title: 'global.error_title', message: 'sign_in_page.form.error.recaptcha' })
      } else {
        const origin = searchParams.get('origin')
        setGlobalErrorMessage({
          title: 'global.error_title',
          message: origin ? 'global.unknown_error_message_origin' : 'global.unknown_error_message',
        })
        console.error(`SignInPage: Unknown value for query param "error_code": ${errorCode}`)
        Sentry.captureMessage('SignInPage: Unknown value for query param "error_code"')
      }
    }
  }, [searchParams])

  // Don't manage access tokens and redirect on partner sign in
  if (!isPartnerSignIn()) {
    // get tokens from URL
    const accessTokenFromUrl = searchParams.get('access_token')
    if (accessTokenFromUrl) {
      saveTokensAndRedirect(accessTokenFromUrl, searchParams.get('refresh_token'))
      return <Loader />
    }

    // authenticate with refresh token
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      exchangeToken(refreshToken)
        .then((tokens) => saveTokensAndRedirect(tokens.access_token, tokens.refresh_token))
        .catch((error) => {
          // failure to exchange a refresh token mostly happens when a Swiler switches environment
          console.error(error)
          clearSavedTokens()
          redirectToUrl(window.location.href)
        })
      return <Loader />
    }
  }

  return (
    <Layout>
      {showAuthenticationCodeForm ? (
        <MfaForm onSubmit={onMfaFormSubmit} />
      ) : (
        <CredentialsForm onSubmit={onCredentialsFormSubmit} />
      )}
      {globalErrorMessage && <AlertError globalErrorMessage={globalErrorMessage} />}
    </Layout>
  )
}
